import cn from 'classnames'
import {FC, ReactNode} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {FormContainer} from '@/components/common/form/FormContainer'
import {FormLayout} from '@/components/common/form/FormLayout'
import {ModalFullScreen} from '@/components/common/modal/ModalFullScreen'
import {useCommonContent} from '@/content/common'
import {useTranslation} from '@/i18n'
import {EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {
  FORM_CONTAINER_PADDING,
  FORM_CONTAINER_PADDING_MOBILE,
  MODAL_HEADER_HEIGHT,
} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE, USER_TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'

type AllMarketsModalProps = {
  title: string
  description?: ReactNode
  closeModal: () => void
  isVisible: boolean
  numOfSelectedJurisdictions: number
  summary: ReactNode
}

export const AllMarketsModal: FC<React.PropsWithChildren<AllMarketsModalProps>> = ({
  title,
  description,
  closeModal,
  isVisible,
  numOfSelectedJurisdictions,
  summary,
  children,
}) => {
  const {t} = useTranslation()
  const {saveAndContinueButton, allCountriesServedTitle} = useCommonContent()

  return (
    <>
      <ModalFullScreen closeModal={closeModal} isVisible={isVisible}>
        <div className="mobile">{summary}</div>
        <FormContainer>
          <Text variant={TextVariant.h2} align="center">
            {title}
          </Text>
          {description && <div className="description">{description}</div>}
        </FormContainer>
        <div className="content">
          <FormLayout
            isModal
            content={
              <>
                <div className="title">
                  <Text variant={TextVariant.h5} element="h3" fontWeight={TextFontWeight.bold}>
                    {t(allCountriesServedTitle)}
                  </Text>
                </div>
                {children}
                <div
                  className={cn(
                    'floating-button',
                    isVisible && numOfSelectedJurisdictions > 0 && 'visible',
                  )}
                >
                  <Button
                    data-test="saveMarketsButton"
                    variant={ButtonVariant.primary}
                    type="button"
                    fullWidth
                    onClick={closeModal}
                  >
                    {t(saveAndContinueButton)}
                  </Button>
                </div>
              </>
            }
            sidebar={<div className="desktop">{summary}</div>}
          />
        </div>
      </ModalFullScreen>

      <style jsx>{`
        .description {
          text-align: center;
          margin-top: ${theme.spacing[2]};
        }

        .content {
          display: flex;
          justify-content: space-between;
        }

        .wrapper {
          margin-top: ${theme.spacing[5]};
        }

        .floating-button {
          position: sticky;
          bottom: calc(-${theme.spacing[7]} * 2 - ${theme.spacing[6]});
          margin-top: ${theme.spacing[3]};
          margin-bottom: -${theme.spacing[7]};
          opacity: 0;
          visibility: hidden;
          transition: all ${EXPAND_TRANSITION}ms ease-in-out;
        }

        .visible {
          bottom: -${theme.spacing[7]};
          opacity: 1;
          visibility: visible;
        }

        .mobile {
          display: none;
        }

        .title {
          margin-bottom: ${theme.spacing[3]};
        }

        @media ${USER_TABLET_SCREENSIZE} {
          .desktop,
          .title {
            display: none;
          }

          .mobile {
            display: block;
            position: sticky;
            top: ${MODAL_HEADER_HEIGHT};
            z-index: 1;
            padding: 0 ${FORM_CONTAINER_PADDING};
            margin-bottom: ${theme.spacing[6]};
          }

          .content {
            margin-top: ${theme.spacing[6]};
          }
        }

        @media ${MOBILE_SCREENSIZE} {
          .mobile {
            padding: 0 ${FORM_CONTAINER_PADDING_MOBILE};
            margin-bottom: ${theme.spacing[5]};
          }

          .content {
            margin-top: ${theme.spacing[5]};
          }

          .visible {
            margin-bottom: -${theme.spacing[5]};
          }
        }
      `}</style>
    </>
  )
}
